import React, { useState, useEffect, useReducer, useRef } from "react";
import Select from "react-select";
import { ReactComponent as People } from "../../assets/img/management_img.svg";
import { BASE_URL, DEVELOPMENT } from "../../Config/api.config";
import useLocalStorage from "../../Hooks/useLocalStorage";
import ModalManagement from "../Modals/ModalManagement";
import RequestErrorModal from "../RequestErrorPopup";
import { Edit, Search, SortAsc, Trash } from "lucide-react";
import spain from "../../assets/img/Flag_of_Spain.svg.webp";
import uk from "../../assets/img/Flag_of_the_United_Kingdom_svg.png";

const initialState = {
  newSkill: "",
  newCategory: "",
  showSkillInput: false,
  showCatInput: false,
  searchCategory: "",
  searchSkill: "",
  sortCategories: "asc",
  sortSkills: "asc",
};

const Management = () => {
  const [allSkills, setAllSkills] = useState([]);
  const [allCategories, setAllCategories] = useState([]);
  const [filteredCategories, setFilteredCategories] = useState([]);
  const [filteredSkills, setFilteredSkills] = useState([]);
  const [language, setLanguage] = useState("EN");
  const [adminData] = useLocalStorage("adminData", "");
  const [showItem, setShowItem] = useState({ edit: -1, trash: -1, where: "" });
  const [alert, setAlert] = useState(false);
  const [typeOfAlert, setTypeOfAlert] = useState("");
  const [state, dispatch] = useReducer(reducer, initialState);
  const [showModal, setShowModal] = useState({
    edit: false,
    delete: false,
    create: false,
    section: "",
  });
  const [refreshData, setRefreshData] = useState(false);
  const [warning, setWarning] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedSkill, setSelectedSkill] = useState(null);
  const skillRef = useRef();
  const catRef = useRef();

  function reducer(state, action) {
    switch (action.type) {
      case "ADD":
        return { ...state, [action.key]: action.value };
      case "TOGGLE_INPUT":
        return { ...state, [action.key]: action.value };
      case "TOGGLE_SORT":
        return { ...state, [action.key]: action.value };
      default:
        return state;
    }
  }

  const sortItems = (items, order) =>
    [...items].sort(
      (a, b) =>
        (a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1) *
        (order === "asc" ? 1 : -1)
    );

  const filterItems = (items, searchTerm) =>
    !searchTerm
      ? items
      : items.filter((i) =>
          i.name.toLowerCase().includes(searchTerm.toLowerCase())
        );

  useEffect(() => {
    setFilteredCategories(
      filterItems(
        sortItems(allCategories, state.sortCategories),
        state.searchCategory
      )
    );
  }, [allCategories, state.sortCategories, state.searchCategory]);

  useEffect(() => {
    setFilteredSkills(
      filterItems(sortItems(allSkills, state.sortSkills), state.searchSkill)
    );
  }, [allSkills, state.sortSkills, state.searchSkill]);

  const getAllCategories = () => {
    fetch(`${BASE_URL}adm.job.categories?language_code=${language}`, {
      method: "GET",
      headers: { Authorization: `Bearer ${adminData.uid}` },
    })
      .then((res) => res.json())
      .then((data) => setAllCategories(data.result || []))
      .catch((err) => console.log(err));
  };

  const getSkillsFromCategory = (id) => {
    fetch(
      `${BASE_URL}adm.job.skills?category_id=${id}&language_code=${language}`,
      {
        method: "GET",
        headers: { Authorization: `Bearer ${adminData.uid}` },
      }
    )
      .then((res) => res.json())
      .then((data) => setAllSkills(data.result || []))
      .catch((err) => console.log(err));
  };

  useEffect(() => {
    getAllCategories();
  }, [language]);

  useEffect(() => {
    if (refreshData) {
      getAllCategories();
      setRefreshData(false);
    }
  }, [refreshData]);

  useEffect(() => {
    if (selectedCategory?.category_id) {
      getSkillsFromCategory(selectedCategory.category_id);
    } else {
      setAllSkills([]);
      setFilteredSkills([]);
    }
  }, [selectedCategory]);

  const showWarning = (msg) => {
    setWarning(msg);
    setTimeout(() => setWarning(false), 3800);
  };

  const submitCategory = (actionType, payload, categoryId = null) => {
    // Asegura que usamos el ID correcto
    const id = categoryId || selectedCategory?.category_id;

    if (actionType === "edit" && !id) {
      showWarning("No category selected for editing");
      return;
    }

    if (actionType === "delete") {
      if (!id) {
        showWarning("No category selected for deletion");
        return;
      }

      fetch(`${BASE_URL}adm.job.categories?category_id=${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${adminData.uid}` },
      })
        .then((res) => res.json())
        .then((data) => {
          if (data.status === "ok") setRefreshData(true);
          else showWarning(data.result?.error_msg || "Error");
        })
        .catch((err) => console.log(err));

      return;
    }

    // Crear o editar
    const formData = new FormData();
    formData.append("_method", actionType === "edit" ? "PUT" : "POST");
    if (actionType === "edit") formData.append("category_id", id); // ID correcto
    formData.append("name_en", payload.en || "");
    formData.append("name_es", payload.es || "");

    fetch(`${BASE_URL}adm.job.categories`, {
      method: "POST",
      headers: { Authorization: `Bearer ${adminData.uid}` },
      body: formData,
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.status === "ok") setRefreshData(true);
        else showWarning(data.result?.error_msg || "Error");
      })
      .catch((err) => console.log(err));
  };

  const submitSkill = (actionType, payload, skillId = null) => {
    if (!selectedCategory && actionType !== "delete") {
      showWarning("Please select a category first");
      return;
    }

    const formData = new FormData();
    formData.append("_method", actionType === "edit" ? "PUT" : "POST");
    if (actionType === "edit" && skillId) formData.append("skill_id", skillId);
    if (actionType !== "delete") {
      formData.append("category_id", selectedCategory.category_id);
      formData.append("name", payload.en || "");
      formData.append("language_code", DEVELOPMENT === 2 ? "ES" : "EN");
    }

    fetch(`${BASE_URL}adm.job.skills`, {
      method: "POST",
      headers: { Authorization: `Bearer ${adminData.uid}` },
      body: formData,
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.status === "ok") {
          if (actionType !== "delete")
            getSkillsFromCategory(selectedCategory.category_id);
        } else showWarning(data.result?.error_msg || "Error");
      })
      .catch((err) => console.log(err));
  };

  const handleAction = (idOrPayload, payload) => {
    const section = showModal.section;
    const type = showModal.create
      ? "create"
      : showModal.edit
      ? "edit"
      : "delete";

    if (section === "category") {
      const idToUse =
        type === "edit" ? idOrPayload || selectedCategory?.category_id : null;
      submitCategory(type, payload, idToUse);
    } else if (section === "skill") {
      const idToUse =
        type === "edit" ? idOrPayload || selectedSkill?.skill_id : null;
      submitSkill(type, payload, idToUse);
    }
  };

  const colourStyles = {
    control: (styles) => ({
      ...styles,
      border: "1px solid #1d90b3",
      fontWeight: "300",
      color: "black",
      letterSpacing: "0.05em",
      backgroundColor: "#F9F7F7",
      padding: "0.5rem",
      borderRadius: "5px",
      height: "40px",
      boxShadow: "none",
      "&:hover": { border: "1px solid #1d90b3" },
    }),
    option: (styles, { isFocused }) => ({
      ...styles,
      backgroundColor: isFocused ? "#e6f7ff" : null,
      color: isFocused ? "#1d90b3" : "black",
    }),
  };

  return (
    <div className="relative">
      <div className="py-8 px-8 flex flex-col w-full bg-white">
        {/* Alert */}
        <div
          className={`fixed top-4 left-1/2 transform -translate-x-1/2 z-50 transition-opacity duration-700 ${
            !alert ? "opacity-0" : "opacity-100"
          }`}
        >
          <div className="bg-[#2563EB] text-white font-semibold text-sm rounded-xl py-3.5 px-6 shadow-lg flex items-center gap-4">
            <p>{typeOfAlert}</p>
          </div>
        </div>

        {/* Error Modal */}
        <div className="absolute top-0 left-[50%]">
          <RequestErrorModal warning={warning} />
        </div>

        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <People className="h-10 w-fit text-[#2563EB]" />
          <p className="font-bold tracking-wide text-3xl text-gray-800">
            MANAGEMENT
          </p>
        </div>

        <div className="flex flex-col lg:flex-row gap-8 px-4 mt-6">
          {/* Categories Section */}
          <div className="flex-1 bg-white h-full rounded-2xl shadow-lg p-6 border border-gray-100">
            <div className="flex items-center justify-between mb-6">
              <p className="font-bold text-xl text-[#2563EB]">Categories</p>
              {!state.showCatInput ? (
                <button
                  className="bg-[#ed4685] text-white px-4 h-10 rounded-lg hover:bg-[#2563EB] transition-colors"
                  onClick={() =>
                    setShowModal({ create: true, section: "category" })
                  }
                >
                  + Add a category
                </button>
              ) : (
                <div className="relative h-10 w-[205px]">
                  <input
                    type="text"
                    autoFocus
                    ref={catRef}
                    placeholder="Enter a category"
                    value={state.newCategory}
                    onChange={(e) =>
                      dispatch({
                        type: "ADD",
                        value: e.target.value,
                        key: "newCategory",
                      })
                    }
                    className="h-full w-full px-4 pr-16 rounded-lg border border-gray-300 bg-gray-50 text-sm outline-none focus:ring-2 focus:ring-[#2563EB] focus:border-[#2563EB]"
                  />
                  <button
                    className={`absolute right-0 top-1/2 -translate-y-1/2 h-full px-3 text-xs font-medium rounded-r-lg transition-colors ${
                      !state.newCategory
                        ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                        : "bg-[#2563EB] text-white hover:bg-[#ed4685]"
                    }`}
                    disabled={!state.newCategory}
                    onClick={() => {
                      dispatch({
                        type: "TOGGLE_INPUT",
                        value: false,
                        key: "showCatInput",
                      });
                      dispatch({ type: "ADD", value: "", key: "newCategory" });
                      handleAction(null, {
                        en: state.newCategory,
                        es: state.newCategory,
                      });
                    }}
                  >
                    ADD
                  </button>
                </div>
              )}
            </div>

            {/* Search & Sort */}
            <div className="flex items-center justify-between mb-4">
              <div className="relative w-64">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Search className="h-4 w-4 text-gray-400" />
                </div>
                <input
                  type="text"
                  placeholder="Search categories..."
                  value={state.searchCategory}
                  onChange={(e) =>
                    dispatch({
                      type: "ADD",
                      value: e.target.value,
                      key: "searchCategory",
                    })
                  }
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-sm focus:outline-none focus:ring-2 focus:ring-[#2563EB] focus:border-[#2563EB]"
                />
              </div>
              <div className="flex items-center gap-3">
                {/* Selector de idioma */}
                <div className="flex gap-2">
                  <button
                    onClick={() => setLanguage("EN")}
                    className={`w-8 h-8 rounded-full border ${
                      language === "EN" ? "ring-2 ring-blue-500" : "opacity-60"
                    }`}
                    title="English"
                  >
                    <img
                      src={uk}
                      alt="UK Flag"
                      className="w-full h-full rounded-full"
                    />
                  </button>
                  <button
                    onClick={() => setLanguage("ES")}
                    className={`w-8 h-8 rounded-full border ${
                      language === "ES" ? "ring-2 ring-red-500" : "opacity-60"
                    }`}
                    title="Español"
                  >
                    <img
                      src={spain}
                      alt="Spain Flag"
                      className="w-full h-full rounded-full"
                    />
                  </button>
                </div>
                <button
                  className="flex items-center text-sm text-gray-600 hover:text-gray-800"
                  onClick={() =>
                    dispatch({
                      type: "TOGGLE_SORT",
                      value: state.sortCategories === "asc" ? "desc" : "asc",
                      key: "sortCategories",
                    })
                  }
                >
                  <SortAsc className="h-4 w-4 mr-1" />
                  {state.sortCategories === "asc" ? "A-Z" : "Z-A"}
                </button>
              </div>
            </div>

            {/* Categories List */}
            <div className="bg-gray-50 rounded-xl shadow-inner p-4 h-[50vh] overflow-hidden flex flex-col">
              <div className="text-xs text-gray-500 mb-2">
                {filteredCategories.length} categories
              </div>
              <div className="overflow-y-auto flex-grow">
                {filteredCategories.length > 0 ? (
                  filteredCategories.map((category, i) => (
                    <div
                      key={category.category_id}
                      className={`group hover:bg-[#ed4685]/10 cursor-pointer rounded-lg py-2 px-3 flex justify-between items-center transition-colors ${
                        selectedCategory?.name === category?.name
                          ? "bg-[#2563EB]/10 font-semibold"
                          : ""
                      }`}
                      onClick={() => setSelectedCategory(category)}
                      onMouseEnter={() =>
                        setShowItem({
                          ...showItem,
                          trash: i,
                          edit: i,
                          where: "cat",
                        })
                      }
                      onMouseLeave={() =>
                        setShowItem({
                          ...showItem,
                          trash: -1,
                          edit: -1,
                          where: "",
                        })
                      }
                    >
                      <p className="text-gray-800">
                        {category.name} ({category.category_id})
                      </p>

                      <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                        {showItem.edit === i && showItem.where === "cat" && (
                          <button
                            className="flex items-center justify-center h-8 w-8 bg-[#2563EB] rounded-full hover:bg-[#1e40af] transition-colors shadow-md"
                            onClick={(e) => {
                              e.stopPropagation();
                              setShowModal({
                                edit: true,
                                section: "category",
                                category,
                              });
                            }}
                          >
                            <Edit className="h-4 w-4 text-white" />
                          </button>
                        )}
                        {showItem.trash === i && showItem.where === "cat" && (
                          <button
                            className="flex items-center justify-center h-8 w-8 bg-[#ed4685] rounded-full hover:bg-[#c63d76] transition-colors shadow-md"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedCategory(category); // ← esto asegura que category_id exista
                              setShowModal({
                                delete: true,
                                section: "category",
                              });
                            }}
                          >
                            <Trash className="h-4 w-4 text-white" />
                          </button>
                        )}
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-8 text-gray-400">
                    {state.searchCategory
                      ? "No categories match your search"
                      : "No categories found"}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Skills Section */}
          <div className="flex-1 bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
            <div className="flex items-center justify-between mb-6">
              <p className="font-bold text-xl text-[#2563EB]">Skills</p>
              {!state.showSkillInput ? (
                <button
                  className="bg-[#ed4685] text-white px-4 h-10 rounded-lg hover:bg-[#2563EB] transition-colors"
                  onClick={() =>
                    setShowModal({ create: true, section: "skill" })
                  }
                >
                  + Add a skill
                </button>
              ) : (
                <div className="flex gap-2">
                  <Select
                    defaultValue={selectedCategory}
                    styles={colourStyles}
                    options={allCategories}
                    className="text-sm w-48"
                    onChange={(e) => setSelectedCategory(e)}
                    placeholder="Select category"
                    isSearchable
                  />
                  <div className="relative">
                    <input
                      type="text"
                      autoFocus
                      ref={skillRef}
                      placeholder="Enter a skill"
                      value={state.newSkill}
                      onChange={(e) =>
                        dispatch({
                          type: "ADD",
                          value: e.target.value,
                          key: "newSkill",
                        })
                      }
                      className="h-10 w-48 px-4 pr-16 rounded-lg border border-gray-300 bg-gray-50 text-sm outline-none focus:ring-2 focus:ring-[#2563EB] focus:border-[#2563EB]"
                    />
                    <button
                      className={`absolute right-0 top-1/2 -translate-y-1/2 h-full px-3 text-xs font-medium rounded-r-lg transition-colors ${
                        !state.newSkill || !selectedCategory
                          ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                          : "bg-[#2563EB] text-white hover:bg-[#ed4685]"
                      }`}
                      disabled={!state.newSkill || !selectedCategory}
                      onClick={() => {
                        handleAction(null, { en: state.newSkill });
                        dispatch({ type: "ADD", value: "", key: "newSkill" });
                      }}
                    >
                      ADD
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Search & Sort */}
            <div className="flex items-center justify-between mb-4">
              <div className="relative w-64">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Search className="h-4 w-4 text-gray-400" />
                </div>
                <input
                  type="text"
                  placeholder="Search skills..."
                  value={state.searchSkill}
                  onChange={(e) =>
                    dispatch({
                      type: "ADD",
                      value: e.target.value,
                      key: "searchSkill",
                    })
                  }
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-sm focus:outline-none focus:ring-2 focus:ring-[#2563EB] focus:border-[#2563EB]"
                />
              </div>
              <button
                className="flex items-center text-sm text-gray-600 hover:text-gray-800"
                onClick={() =>
                  dispatch({
                    type: "TOGGLE_SORT",
                    value: state.sortSkills === "asc" ? "desc" : "asc",
                    key: "sortSkills",
                  })
                }
              >
                <SortAsc className="h-4 w-4 mr-1" />
                {state.sortSkills === "asc" ? "A-Z" : "Z-A"}
              </button>
            </div>

            {/* Skills List */}
            <div className="bg-gray-50 rounded-xl shadow-inner p-4 h-[50vh] overflow-hidden flex flex-col">
              <div className="text-xs text-gray-500 mb-2 flex justify-between">
                <span>{filteredSkills.length} skills</span>
                {selectedCategory && (
                  <span className="font-medium">
                    in {selectedCategory.name}
                  </span>
                )}
              </div>
              <div className="overflow-y-auto flex-grow">
                {filteredSkills.length > 0 ? (
                  filteredSkills.map((skill, i) => (
                    <div
                      key={skill.skill_id}
                      className="group hover:bg-[#ed4685]/10 cursor-pointer rounded-lg py-2 px-3 flex justify-between items-center transition-colors"
                      onMouseEnter={() =>
                        setShowItem({
                          ...showItem,
                          trash: i,
                          edit: i,
                          where: "skill",
                          skill,
                        })
                      }
                      onMouseLeave={() =>
                        setShowItem({
                          ...showItem,
                          trash: -1,
                          edit: -1,
                          where: "",
                          skill,
                        })
                      }
                    >
                      <p className="text-gray-800">
                        {skill.name} ({skill.skill_id})
                      </p>
                      <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                        {showItem.edit === i && showItem.where === "skill" && (
                          <button
                            className="flex items-center justify-center h-8 w-8 bg-[#2563EB] rounded-full hover:bg-[#1e40af] transition-colors shadow-md"
                            onClick={() => {
                              setSelectedSkill(skill);
                              setShowModal({
                                edit: true,
                                section: "skill",
                                skill,
                              });
                            }}
                          >
                            <Edit className="h-4 w-4 text-white" />
                          </button>
                        )}
                        {showItem.trash === i && showItem.where === "skill" && (
                          <button
                            className="flex items-center justify-center h-8 w-8 bg-[#ed4685] rounded-full hover:bg-[#c63d76] transition-colors shadow-md"
                            onClick={() => {
                              setSelectedSkill(skill);
                              setShowModal({
                                delete: true,
                                section: "skill",
                                skill,
                              });
                            }}
                          >
                            <Trash className="h-4 w-4 text-white" />
                          </button>
                        )}
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-8 text-gray-400">
                    {state.searchSkill
                      ? "No skills match your search"
                      : selectedCategory
                      ? "No skills found for this category"
                      : "Select a category to view skills"}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modals */}
      {showModal.create && (
        <ModalManagement
          type="create"
          section={showModal.section}
          showModal={showModal}
          setShowModal={setShowModal}
          selectedCategory={selectedCategory}
          selectedSkill={selectedSkill}
          handleAction={handleAction}
        />
      )}
      {showModal.edit && (
        <ModalManagement
          type="edit"
          section={showModal.section}
          showModal={showModal}
          setShowModal={setShowModal}
          selectedCategory={selectedCategory}
          selectedSkill={selectedSkill}
          handleAction={handleAction}
        />
      )}
      {showModal.delete && (
        <ModalManagement
          type="delete"
          section={showModal.section}
          showModal={showModal}
          setShowModal={setShowModal}
          selectedCategory={selectedCategory}
          selectedSkill={selectedSkill}
          handleAction={handleAction}
        />
      )}

      {showModal.create && showModal.section === "skill" && (
        <ModalManagement
          type="create"
          section="skill"
          showModal={showModal}
          setShowModal={setShowModal}
          selectedCategory={selectedCategory}
          handleAction={handleAction}
        />
      )}

      {showModal.edit && showModal.section === "skill" && (
        <ModalManagement
          type="edit"
          section="skill"
          showModal={showModal}
          setShowModal={setShowModal}
          selectedSkill={selectedSkill}
          selectedCategory={selectedCategory}
          handleAction={handleAction}
        />
      )}

      {showModal.delete && showModal.section === "skill" && (
        <ModalManagement
          type="delete"
          section="skill"
          showModal={showModal}
          setShowModal={setShowModal}
          selectedSkill={selectedSkill}
          selectedCategory={selectedCategory}
          handleAction={handleAction}
        />
      )}
    </div>
  );
};

export default Management;
